import React, {useEffect, useState} from "react";

import {DeleteOutlined, FolderOpenOutlined} from "@ant-design/icons";
import {Button, message, Statistic, Tooltip} from "antd";
import Search from "antd/es/input/Search";

import {CreateScope} from "./CreateScope";
import {ImportScopeModal} from "./ImportScopeModal";
import {ScopeRowActions} from "./ScopeRowActions";
import api from "../../api";
import {checkAnyOfPermissions, createScopeLink, normalizeSearchQuery} from "../../helpers";
import {DataCard} from "../helpers/cards/Card";
import {StandardTable} from "../helpers/TablePanel";
import {UnzipScopeModal} from "../helpers/modals/UnzipScopeModal"
import {extractErrorMessage} from "../../index";

export function ArchiveScopesList({projectId, activeKey}) {
	const [isAdmin, setIsAdmin] = useState(false);
	const [isSuperAdmin, setIsSuperAdmin] = useState(false);
	const [startUpdate, setStartUpdate] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [archivedByFilterOptions, setArchivedByFilterOptions] = useState([]);
	const [scopeTypeOptions, setScopeTypeOptions] = useState([]);
	const [selectedScope, setSelectedScope] = useState(null);
	const [isImportModalVisible, setIsImportModalVisible] = useState(false);
	const [isUnzipModalVisible, setUnzipModalVisible] = useState(false);
	const [loading, setLoading] = useState(false);

	const style = {
		height: "80vh",
		maxHeight: "80vh",
	};

	useEffect(() => {
		checkAnyOfPermissions(["senior-user", "approve-user", "insurance-admin", "admin", "material-analyst"]).then(
			() => setIsAdmin(true),
		);
		checkAnyOfPermissions(["superadmin"]).then(() => setIsSuperAdmin(true));

		getOptionsForFilter("type", setScopeTypeOptions);
		getOptionsForFilter("last_change_by", setArchivedByFilterOptions);
	}, [projectId]);

	const getOptionsForFilter = (fieldName, setFilterOptions) => {
		if (projectId) {
			api.get(`/projects/${projectId}/scopes/filter/options?field_name=${encodeURI(fieldName)}&archived=True`)
			.then(json =>
				setFilterOptions(json.data),
			);
		}
	};

	const columns = [
		{
			title: "Scope Name",
			dataIndex: "name",
			key: "name",
			render: (name, item) => <a href={createScopeLink(projectId, item._id, true)}>{name}</a>,
		},
		{
			title: "Scope Description",
			dataIndex: "description",
			key: "description",
		},
		{
			title: "Archived By",
			dataIndex: "last_change_by",
			key: "last_change_by",
			filters: archivedByFilterOptions.map(option => ({
				text: option,
				value: option,
			})),
		},
		{
			title: "Archive date",
			dataIndex: "last_change_date",
			key: "last_change_date",
		},
		{
			title: "Additional Info",
			dataIndex: "add_info",
			key: "add_info",
		},
		{
			title: "Items in Scope",
			dataIndex: "quantity",
			key: "quantity",
			render: quantity => <Statistic value={quantity} valueStyle={{fontSize: 14}} />,
		},
		{
			title: "Actions",
			key: "actions",
			width: 180,
			render: (_, item) => {
				const hasPermission = item.type === "main-scope" ? isSuperAdmin : isAdmin;
				if (!hasPermission) return null;

				return (
					<ScopeRowActions>
						<Tooltip title="Unarchive">
							<span>
								<FolderOpenOutlined
									className={"beautiful-link"}
									style={{fontSize: 18, cursor: "pointer"}}
									onClick={() => UnzipScope(item)}
								/>
							</span>
						</Tooltip>
						<Tooltip title="Delete">
							<span>
								<DeleteOutlined
									className={"beautiful-link"}
									onClick={() => deleteScope(item._id)}
									style={{fontSize: 18}}
								/>
							</span>
						</Tooltip>
					</ScopeRowActions>
				);
			},
		},
	].filter(Boolean);

	const deleteScope = _id => {
		api.get(`/projects/${projectId}/scopes/delete?id=${_id}&archived=True`, {omitGlobalError: true})
			.then(() => {
				message.success(`The scope was successfully deleted`);
				setStartUpdate(!startUpdate);
			})
			.catch(async error => {
				console.log(error);
				const msg = await extractErrorMessage(error);
				message.error(msg);
			});
	};

	const onFinish = () => {
		setIsImportModalVisible(false);
		setStartUpdate(!startUpdate);
		setLoading(false);
	};

	const UnzipScope = scope => {
	    setUnzipModalVisible(true);
	    setSelectedScope(scope);
    };

    const onFinishUnzip = (values, _id) => {
		api.post(
		    `/projects/${projectId}/scopes/unzip?id=${_id}&email=${values?.archived_by}&add_info=${values?.add_info}`
		, {}, {omitGlobalError: true})
        .then(res => {
            message.success(res.data);
            setStartUpdate(!startUpdate);
            setUnzipModalVisible(false);
        })
        .catch(async error => {
            console.log(error);
            const msg = await extractErrorMessage(error);
            message.error(msg);
        });
    };

	return (
		<DataCard style={style}>
			{isAdmin && (
				<div style={{paddingBottom: 10, float: "right"}}>
					<Button
						onClick={() => {
							setIsModalVisible(true);
						}}>
						Add new scope
					</Button>
				</div>
			)}
			<ScopesTable
				columns={columns}
				projectId={projectId}
				isModalVisible={isModalVisible}
				activeKey={activeKey}
				update={startUpdate}
			/>

			<CreateScope
				isSuperAdmin={isSuperAdmin}
				isModalVisible={isModalVisible}
				scope={selectedScope}
				setSelectedScope={setSelectedScope}
				setIsModalVisible={value => {
					setSelectedScope(null);
					setIsModalVisible(value);
				}}
				projectId={projectId}
				archived={true}
			/>

			<ImportScopeModal
				isSuperAdmin={isSuperAdmin}
				isModalVisible={isImportModalVisible}
				setIsModalVisible={setIsImportModalVisible}
				onFinish={onFinish}
				projectId={projectId}
				spin={loading}
				setLoading={setLoading}
			/>

			<UnzipScopeModal
				isSuperAdmin={isSuperAdmin}
				isModalVisible={isUnzipModalVisible}
				scope={selectedScope}
				setIsModalVisible={setUnzipModalVisible}
				onFinish={onFinishUnzip}
				spin={loading}
				setLoading={setLoading}
			/>
		</DataCard>
	);
}

export function ScopesTable({columns, projectId, isModalVisible, activeKey, update}) {
	const [scopes, setScopes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [total, setTotal] = useState(0);
	const [filters, setFilters] = useState({});
	const [query, setQuery] = useState("");

	const getScopes = (_page = page, size = pageSize, _filters = filters, _query = query) => {
		setLoading(true);
		if (size !== pageSize || _filters !== filters || query !== _query) {
			_page = 1;
		}
		api.post(`/projects/${projectId}/scopes?page=${_page}&size=${size}&archived=true`, {_filters, _query})
			.then(json => {
				setScopes(json.data.items);
				setTotal(json.data.total);
				setPage(_page);
				setQuery(_query);
				setFilters(_filters);
				setPageSize(size);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		if (projectId) {
			getScopes(1);
		}
	}, [projectId, isModalVisible, activeKey, update]);

	const onChange = (newPagination, _filters, _sorter) => {
		getScopes(newPagination.current, newPagination.pageSize, _filters);
	};

	return (
		<div>
			<div style={{marginBottom: 10}}>
				<Search enterButton="Search" onSearch={value => getScopes(page, pageSize, filters, normalizeSearchQuery(value))} />
			</div>
			{query && (
				<div style={{marginBottom: 5}}>
					Current Query: <b>{query}</b>
				</div>
			)}
			<StandardTable
				size={"small"}
				loading={loading}
				style={{width: "100%"}}
				total={total}
				currentPage={page}
				columns={columns}
				scroll={500}
				onChange={onChange}
				pageSize={pageSize}
				tableProps={{
					rowClassName: record => (record.type === "main-scope" ? "main-scope" : ""),
				}}
				data={scopes}
			/>
		</div>
	);
}
